import React from 'react';
import type { OjtMode, OjtModeSignOffConfig, OjtSession } from '../../../types';
import { ArrowRight, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { PinPad, SelfieCapture, SignaturePad } from './SignOffPrimitives';

interface ModeChangeSignOffProps {
  isOpen: boolean;
  session: OjtSession;
  fromMode?: OjtMode;
  toMode: OjtMode;
  config: OjtModeSignOffConfig;
  /** PIN to verify against when method === 'pin'. Caller is responsible for distributing it. */
  pin?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal that gates a candidate-initiated mode switch behind a sign-off step.
 * The lead hand performs the sign-off physically on the candidate's device.
 *
 * Reuses the shared sign-off primitives so the UX matches Handshake and SessionSignOff.
 */
export const ModeChangeSignOff: React.FC<ModeChangeSignOffProps> = ({
  isOpen,
  session,
  fromMode,
  toMode,
  config,
  pin,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-2 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-aero-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-aero-400">Lead hand sign-off required</p>
            <h3 className="text-base font-bold text-aero-900">Confirm mode change</h3>
          </div>
          <button onClick={onCancel} className="p-1 -mr-1 text-aero-400 hover:text-aero-600 transition-colors" aria-label="Cancel">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* From → To summary */}
        <div className="px-5 py-3 bg-aero-50/60 border-b border-aero-100">
          <div className="flex items-center gap-2 text-sm">
            {fromMode ? (
              <>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-medium" style={{ backgroundColor: `${fromMode.color}20`, color: fromMode.color }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: fromMode.color }} />
                  {fromMode.label}
                </span>
              </>
            ) : (
              <span className="text-aero-400 italic text-xs">No active mode</span>
            )}
            <ArrowRight className="w-4 h-4 text-aero-400 flex-shrink-0" />
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-medium" style={{ backgroundColor: `${toMode.color}20`, color: toMode.color }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: toMode.color }} />
              {toMode.label}
            </span>
          </div>
          <p className="text-xs text-aero-500 mt-2">Pass this device to <strong className="text-aero-700">{session.leadHandName}</strong> to confirm.</p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {config.method === 'signature' && (
            <SignaturePad
              title={`${session.leadHandName} — sign here`}
              hint="Lead hand signs above"
              submitLabel="Confirm mode change"
              compact
              onComplete={() => onConfirm()}
            />
          )}

          {config.method === 'pin' && (
            <PinPad
              expectedPin={pin || ''}
              title="Enter lead hand PIN"
              hint="The PIN issued to the lead hand for this session."
              submitLabel="Confirm mode change"
              onComplete={onConfirm}
            />
          )}

          {config.method === 'selfie' && (
            <SelfieCapture
              session={session}
              storagePathSuffix={`mode_change_${toMode.id}_${Date.now()}.jpg`}
              verifyTwoHeads
              title="Two-headed selfie"
              hint="Lead hand and candidate both visible in the frame."
              onComplete={() => onConfirm()}
            />
          )}

          {config.method === 'qr' && (
            <div className="space-y-3">
              <div className="bg-white rounded-xl border border-aero-200 p-5 text-center">
                <h2 className="text-lg font-bold text-aero-900 mb-1">Show this QR to the lead hand</h2>
                <p className="text-sm text-aero-500">Lead hand scans to confirm the mode change.</p>
              </div>
              <div className="bg-white rounded-xl border border-aero-200 p-6 flex flex-col items-center">
                <QRCodeSVG value={`soloready-ojt-mode-change:${session.id}:${toMode.id}:${Date.now()}`} size={192} level="M" />
              </div>
              {/*
                QR verification is not yet wired up to a real round-trip confirmation listener.
                Disable the manual button so candidates can't self-confirm — QR method should be
                treated as unimplemented until the listener lands. Tracked as follow-up.
              */}
              <button
                type="button"
                disabled
                className="w-full py-3 bg-aero-200 text-aero-500 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-not-allowed"
              >
                Waiting for QR verification…
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
